import React from "react";
import { RedisAdminData } from "../types";

interface RedisTabProps {
  data: RedisAdminData;
}

function safeParse(v: any): { CREATED_AT?: string; unlock?: boolean } | null {
  try {
    return typeof v === "string" ? JSON.parse(v) : v;
  } catch {
    return null;
  }
}

export const RedisTab: React.FC<RedisTabProps> = ({ data }) => {
  return (
    <div className="space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        {/* 1. 종목 데이터 요청 목록 (통합) */}
        <section className="glass-card">
          <h2 className="text-2xl font-semibold mb-6 text-white/90 glow-text">
            종목 데이터 요청 목록
          </h2>
          <div className="space-y-4">
            <DataTable
              title="종목 데이터 요청"
              data={data.detailRequest}
              columns={["ticker", "email", "base_dt"]}
            />
            <TickerCountTable
              data={data.detailRequest}
              label="종목 데이터 요청 건수"
            />
          </div>
        </section>

        {/* 2. 종목 리포트 요청 목록 */}
        <section className="glass-card">
          <h2 className="text-2xl font-semibold mb-6 text-white/90 glow-text">
            종목 리포트 요청 목록
          </h2>
          <SimpleCountTable
            data={data.researchReport}
            fieldLabel="티커"
            countLabel="요청 횟수"
          />
        </section>

        {/* 3. 리포트 좋아요 개수 */}
        <section className="glass-card">
          <h2 className="text-2xl font-semibold mb-6 text-white/90 glow-text">
            리포트 좋아요 개수
          </h2>
          <SimpleCountTable
            data={data.agendaLikeCount}
            fieldLabel="리포트ID"
            countLabel="좋아요 수"
          />
        </section>
      </div>
    </div>
  );
};

// 헬퍼 컴포넌트들
function Card({
  title,
  children,
  extra,
}: {
  title: string;
  children: React.ReactNode;
  extra?: React.ReactNode;
}) {
  return (
    <div className="glass-sub-card">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-semibold tracking-tight text-white/90 text-lg glow-text">
          {title}
        </h3>
        {extra && <div className="flex-shrink-0">{extra}</div>}
      </div>
      {children}
    </div>
  );
}

// 포트폴리오 티저 상세 테이블 (CREATED_AT, unlock만 표시)
// 포트폴리오 관련 상세/히트맵/버킷 등은 PortfolioTab로 이동됨

function BucketBar({
  buckets,
  total,
}: {
  buckets: Record<string, number>;
  total: number;
}) {
  const order: Array<keyof typeof buckets> = [
    "0_1d",
    "1_7d",
    "7_14d",
    "14_30d",
    "30p",
  ];
  const colors = ["#06d6a0", "#118ab2", "#073b4c", "#ef476f", "#ffd166"];
  const items = order.map((k, i) => ({
    key: k,
    count: buckets[k] || 0,
    color: colors[i],
  }));
  const sum = items.reduce((a, b) => a + b.count, 0) || 1;

  return (
    <div>
      <div className="flex h-8 w-full rounded-xl overflow-hidden backdrop-blur-sm bg-white/10 border border-white/20 shadow-lg">
        {items.map((it) => (
          <div
            key={String(it.key)}
            className="h-full"
            style={{
              width: `${(it.count / sum) * 100}%`,
              background: `linear-gradient(135deg, ${it.color}dd, ${it.color}aa)`,
              boxShadow: `inset 0 1px 0 rgba(255,255,255,0.2)`,
            }}
            title={`${it.key}: ${it.count}`}
          />
        ))}
      </div>
      <div className="flex flex-wrap gap-4 mt-4 text-sm text-white/80">
        {items.map((it) => (
          <div key={String(it.key)} className="inline-flex items-center gap-2">
            <span
              className="inline-block w-4 h-4 rounded-full border border-white/30 shadow-sm"
              style={{ backgroundColor: it.color }}
            />
            <span className="font-medium">{labelOfBucket(String(it.key))}</span>
            <span className="text-cyan-300 font-mono text-base bg-white/10 px-2 py-0.5 rounded-full">
              {it.count}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

function labelOfBucket(k: string) {
  switch (k) {
    case "0_1d":
      return "최근 1일 이내";
    case "1_7d":
      return "1–7일";
    case "7_14d":
      return "7–14일";
    case "14_30d":
      return "14–30일";
    case "30p":
      return "30일+";
    default:
      return k;
  }
}

function HeatmapLegend() {
  const legendItems = [
    { range: "0", color: "rgba(15, 23, 42, 0.4)", label: "없음" },
    { range: "1-3", color: "rgba(30, 58, 138, 0.5)", label: "매우 낮음" },
    { range: "4-10", color: "rgba(67, 56, 202, 0.7)", label: "낮음" },
    { range: "11-20", color: "rgba(37, 99, 235, 0.8)", label: "보통" },
    { range: "21+", color: "rgba(59, 130, 246, 0.9)", label: "높음" },
  ];

  return (
    <div className="flex items-center gap-3 text-xs">
      {legendItems.map((item, index) => (
        <div key={index} className="flex items-center gap-1">
          <div
            className="w-3 h-3 rounded border border-white/20"
            style={{ backgroundColor: item.color }}
          />
          <span className="text-white/70 whitespace-nowrap">{item.range}</span>
        </div>
      ))}
    </div>
  );
}

function Heatmap({ matrix }: { matrix: number[][] }) {
  // matrix: [weekday(0-6)][hour(0-23)]
  const weekdays = ["일", "월", "화", "수", "목", "금", "토"];
  const hours = Array.from(
    { length: 24 },
    (_, i) => `${String(i).padStart(2, "0")}시`
  );
  const flat = matrix.flat();
  const max = Math.max(1, ...flat);
  const color = (v: number) => {
    if (v === 0) {
      return {
        backgroundColor: "rgba(15, 23, 42, 0.4)", // 어두운 슬레이트 베이스
        boxShadow: "none",
      };
    }

    // 실제 데이터 범위에 맞는 절대값 기준 컬러링
    let backgroundColor, glowColor;

    if (v <= 3) {
      // 매우 낮음 (0-3): 어두운 네이비
      const intensity = Math.min(v / 3, 1);
      backgroundColor = `rgba(30, 58, 138, ${0.3 + intensity * 0.3})`;
      glowColor = `rgba(59, 130, 246, ${intensity * 0.2})`;
    } else if (v <= 10) {
      // 낮음 (4-10): 인디고 블루
      const intensity = Math.min((v - 3) / 7, 1);
      backgroundColor = `rgba(67, 56, 202, ${0.5 + intensity * 0.3})`;
      glowColor = `rgba(99, 102, 241, ${0.3 + intensity * 0.3})`;
    } else if (v <= 20) {
      // 보통 (11-20): 밝은 블루
      const intensity = Math.min((v - 10) / 10, 1);
      backgroundColor = `rgba(37, 99, 235, ${0.7 + intensity * 0.2})`;
      glowColor = `rgba(96, 165, 250, ${0.5 + intensity * 0.3})`;
    } else {
      // 높음 (21+): 매우 밝은 블루 + 강한 글로우
      const intensity = Math.min((v - 20) / 15, 1); // 35까지를 최대로 가정
      backgroundColor = `rgba(59, 130, 246, ${0.8 + intensity * 0.2})`;
      glowColor = `rgba(147, 197, 253, ${0.7 + intensity * 0.3})`;
    }

    return {
      backgroundColor,
      boxShadow:
        v > 5 ? `0 0 ${Math.round((v / 35) * 8 + 2)}px ${glowColor}` : "none",
    };
  };

  return (
    <div className="overflow-auto rounded-2xl bg-white/5 backdrop-blur-sm border border-white/20 shadow-2xl p-2">
      <table className="text-base table-fixed">
        <colgroup>
          <col className="w-16" />
          {Array.from({ length: 24 }).map((_, i) => (
            <col key={i} className="w-8" />
          ))}
        </colgroup>
        <thead>
          <tr>
            <th className="p-2 text-left text-xs text-white/80 font-semibold">
              요일/시간
            </th>
            {hours.map((h) => (
              <th
                key={h}
                className="px-1 py-2 text-white/70 font-normal whitespace-nowrap font-mono text-center text-base"
              >
                {h.replace("시", "")}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {matrix.map((row, wd) => (
            <tr key={wd}>
              <td className="pr-2 py-1 text-white/80 whitespace-nowrap font-medium text-center">
                {weekdays[wd]}
              </td>
              {row.map((v, hr) => (
                <td key={hr} className="p-0.5">
                  <div
                    className="w-7 h-7 rounded-lg mx-auto border border-white/10 cursor-pointer hover:scale-110 transition-all duration-200 hover:border-white/30"
                    title={`${weekdays[wd]}요일 ${hr}시: ${v}건`}
                    style={color(v)}
                  />
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// 종목 데이터 요청 테이블 (요청일 내림차순, 내부 스크롤)
function DataTable({
  title,
  data,
  columns,
}: {
  title: string;
  data: any;
  columns: string[];
}) {
  if (!data || Object.keys(data).length === 0) return null;
  // 모든 요청을 요청일 기준 내림차순으로 정렬
  const rows: {
    ticker: string;
    itemName: string;
    email: string;
    base_dt: string;
  }[] = [];
  Object.entries(data).forEach(([ticker, obj]: any) => {
    const arr = obj.list;
    const itemName = obj.itemName;
    if (Array.isArray(arr)) {
      arr.forEach((item: any) => {
        rows.push({
          ticker,
          itemName,
          email: item.email,
          base_dt: item.base_dt,
        });
      });
    }
  });
  rows.sort((a, b) =>
    a.base_dt < b.base_dt ? 1 : a.base_dt > b.base_dt ? -1 : 0
  );

  return (
    <div>
      <h3 className="font-semibold mb-4 text-white/90 text-lg glow-text">
        {title}
      </h3>
      <div>
        <div className="max-h-96 glass-table-container scrollable-table">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-white/10 backdrop-blur-md z-10">
              <tr>
                <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
                  티커
                </th>
                <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
                  종목명
                </th>
                <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
                  이메일
                </th>
                <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
                  요청일
                </th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, idx) => (
                <tr
                  key={row.ticker + row.email + row.base_dt + idx}
                  className={idx % 2 === 0 ? "bg-white/2" : ""}
                >
                  <td className="px-4 py-3 text-cyan-300 font-mono text-base border-b border-white/10">
                    {row.ticker}
                  </td>
                  <td className="px-4 py-3 text-white/80 border-b border-white/10 text-base">
                    {row.itemName}
                  </td>
                  <td className="px-4 py-3 text-white/70 font-mono text-base border-b border-white/10">
                    {row.email}
                  </td>
                  <td className="px-4 py-3 text-violet-300 font-mono text-base border-b border-white/10">
                    {row.base_dt}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// 티커별 요청 건수 테이블 (내부 스크롤)
function TickerCountTable({ data, label }: { data: any; label: string }) {
  if (!data || Object.keys(data).length === 0) return null;
  const counts = Object.entries(data).map(([ticker, obj]: any) => ({
    ticker,
    itemName: obj.itemName,
    count: Array.isArray(obj.list) ? obj.list.length : 0,
  }));
  // 요청 건수 내림차순 정렬
  counts.sort((a, b) => b.count - a.count);
  return (
    <div className="mt-4">
      <h4 className="font-semibold mb-3 text-white/90 glow-text">{label}</h4>
      <div>
        <div className="max-h-48 glass-table-container scrollable-table">
          <table className="min-w-full text-sm">
            <thead className="sticky top-0 bg-white/10 backdrop-blur-md z-10">
              <tr>
                <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
                  티커
                </th>
                <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
                  종목명
                </th>
                <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
                  요청 건수
                </th>
              </tr>
            </thead>
            <tbody>
              {counts.map((row, index) => (
                <tr
                  key={row.ticker}
                  className={index % 2 === 0 ? "bg-white/2" : ""}
                >
                  <td className="px-4 py-3 text-cyan-300 font-mono text-base border-b border-white/10">
                    {row.ticker}
                  </td>
                  <td className="px-4 py-3 text-white/80 border-b border-white/10 text-base">
                    {row.itemName}
                  </td>
                  <td className="px-4 py-3 text-violet-300 font-bold border-b border-white/10">
                    {row.count}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

// 단순 카운트 테이블 (리포트 요청, 좋아요 등) - 내부 스크롤
function SimpleCountTable({
  data,
  fieldLabel,
  countLabel,
}: {
  data: any;
  fieldLabel: string;
  countLabel: string;
}) {
  if (!data || Object.keys(data).length === 0)
    return (
      <div className="text-center py-8">
        <div className="text-white/60 text-lg glow-text">데이터 없음</div>
      </div>
    );
  // value가 객체면 count와 itemName 분리, 아니면 기존대로
  const rows = Object.entries(data).map(([field, value]: any) => {
    if (typeof value === "object" && value !== null && "itemName" in value) {
      // researchReport: { 0: count, itemName: '종목명' } 형태
      // agendaLikeCount 등은 itemName 없음
      const count = value[0] ?? value.count ?? 0;
      return { field, itemName: value.itemName || "", count };
    }
    return { field, itemName: "", count: value };
  });
  // count 내림차순 정렬
  rows.sort((a, b) => b.count - a.count);
  return (
    <div>
      <div className="max-h-96 glass-table-container scrollable-table">
        <table className="min-w-full text-[18px]">
          <thead className="sticky top-0 bg-white/10 backdrop-blur-md z-10">
            <tr>
              <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
                {fieldLabel}
              </th>
              {fieldLabel === "티커" && (
                <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
                  종목명
                </th>
              )}
              <th className="px-4 py-3 text-white/90 font-semibold text-left border-b border-white/20">
                {countLabel}
              </th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, index) => (
              <tr
                key={row.field}
                className={index % 2 === 0 ? "bg-white/2" : ""}
              >
                <td className="px-4 py-3 text-cyan-300 font-mono text-base border-b border-white/10">
                  {row.field}
                </td>
                {fieldLabel === "티커" && (
                  <td className="px-4 py-3 text-white/80 border-b border-white/10 text-base">
                    {row.itemName}
                  </td>
                )}
                <td className="px-4 py-3 text-violet-300 font-bold border-b border-white/10">
                  {row.count}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
